import { supabase } from "../supabase/SupabaseClient";

/* La revisión de lecturas del OCR de vales — el sector de Igna.
 *
 * EL PROBLEMA QUE RESUELVE: el que saca la foto (portería) no es el que puede
 * juzgar la lectura, y no se le puede pedir que la corrija — bastante hace con
 * sacar la foto. Acá el que revisa ve la FOTO al lado de lo que el bot leyó
 * (`leido`, el JSON crudo del modelo) y de lo que quedó guardado, y deja su
 * veredicto. Ese veredicto es el dataset del que después salen las reglas del
 * prompt (como la de los ceros del 21/08).
 *
 * TRES VEREDICTOS Y NO DOS. «Leyó mal» es el modelo equivocándose en un
 * dígito: se arregla con una regla en el prompt, y la nota dice qué decía el
 * papel. «No leyó» es la foto que ni llegó a lectura —borrosa, mal recortada,
 * o la llamada que falló— y se arregla en otro lado. Con un booleano los dos
 * caían en el mismo `false` y el dataset decía «el OCR falla» sin decir en
 * qué (Igna, 21/08/2026).
 *
 * La pared es la RLS con `ver.lecturas` (hoy, solo Igna): el UPDATE está
 * además capado POR COLUMNA en la base — desde el hub solo se pueden tocar
 * las tres columnas del veredicto, nunca los datos de la carga.
 */

export const ESTADOS = ["bien", "mal", "no_leyo"];

const CAMPOS =
  "id,fecha,interno,litros,vale,km,producto,quien_nombre,fuente," +
  "foto,media_id,leido,lectura_estado,lectura_nota,lectura_revisada_en";

/* Las cargas que tienen algo para revisar: foto o lectura. Una carga dictada
 * por texto sin foto no dice nada del OCR y acá no aporta. */
export async function traerLecturas() {
  const corte = new Date(Date.now() - 90 * 24 * 3600 * 1000).toISOString();
  const { data, error } = await supabase
    .from("urea_cargas")
    .select(CAMPOS)
    .gte("fecha", corte)
    .or("foto.not.is.null,leido.not.is.null")
    .order("fecha", { ascending: false })
    .limit(999);
  if (error) throw error;
  return data || [];
}

/* El link firmado a la foto. El bucket es privado (el vale trae nombre,
 * patente e importe) y la firma dura una hora — mismo criterio que el visor
 * del bot: el que abre la pantalla la ve, el link pegado en un chat se muere
 * solo. La RLS de storage exige `ver.lecturas` para poder firmar. */
export async function fotoUrl(ruta) {
  if (!ruta) return null;
  const { data, error } = await supabase.storage
    .from("vales")
    .createSignedUrl(ruta, 3600);
  if (error) return null;
  return data?.signedUrl || null;
}

/* El veredicto: 'bien' | 'mal' | 'no_leyo'. La nota es lo que el bot tendría
 * que haber leído (o por qué estuvo mal).
 *
 * El estado se valida acá ADEMÁS de en la base. La base ya lo rechaza con un
 * CHECK, pero ese error llega como un 400 de PostgREST que la pantalla mostró
 * como «no se pudo guardar»: un typo en el código se vería como una falla de
 * red intermitente. Mejor que reviente donde está el error.
 *
 * El `.select()` confirma que la base lo tomó: la RLS filtra en silencio y
 * cero filas acá ES el error. */
export async function marcarLectura(id, estado, nota = "") {
  if (!ESTADOS.includes(estado))
    throw new Error(`Veredicto desconocido: ${estado}`);
  const { data, error } = await supabase
    .from("urea_cargas")
    .update({
      lectura_estado: estado,
      lectura_nota: (nota || "").trim() || null,
      lectura_revisada_en: new Date().toISOString(),
    })
    .eq("id", id)
    .select("id");
  if (error) throw error;
  if (!data || data.length === 0)
    throw new Error("No se pudo guardar el veredicto.");
  return data[0];
}
